function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _nonIterableRest() {
  throw new TypeError('Invalid attempt to destructure non-iterable instance');
}

function _iterableToArrayLimit(arr, i) {
  const _arr = [];
  let _n = true;
  let _d = false;
  let _e = undefined;
  try {
    for (let _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i['return'] != null) _i['return']();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError('Cannot call a class as a function');
  }
}

function _defineProperties(target, props) {
  for (let i = 0; i < props.length; i++) {
    const descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ('value' in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true,
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

import { getEntityRanges, BLOCK_TYPE, INLINE_STYLE } from '../../draft-js-utils/src';
import { ENTITY_TYPE } from '../../utils/types';
import { fieldRegex } from '$/shared/frontend/utils/urls/URLUtils';
import linkifyIt from 'linkify-it';
import tlds from 'tlds';

const linkify = linkifyIt();
linkify.tlds(tlds);

const BOLD = INLINE_STYLE.BOLD,
  CODE = INLINE_STYLE.CODE,
  ITALIC = INLINE_STYLE.ITALIC,
  STRIKETHROUGH = INLINE_STYLE.STRIKETHROUGH,
  UNDERLINE = INLINE_STYLE.UNDERLINE;
const CODE_INDENT = '    ';
const defaultOptions = {
  gfm: false,
};

function canHaveDepth(blockType) {
  switch (blockType) {
    case BLOCK_TYPE.UNORDERED_LIST_ITEM:
    case BLOCK_TYPE.ORDERED_LIST_ITEM:
      return true;

    default:
      return false;
  }
}

function encodeContent(text) {
  return text.replace(/[*_`]/g, '\\$&');
}

function encodeCode(text) {
  return text.replace(/`/g, '\\`');
} // Encode chars that would normally be allowed in a URL but would conflict with
// our markdown syntax: `[foo](http://foo/)`

const LINK_CHARACTER_REPLACEMENTS = {
  '(': '%28',
  ')': '%29',
};

function encodeURL(url) {
  return url.replace(/[()]/g, function (_char) {
    return LINK_CHARACTER_REPLACEMENTS[_char];
  });
} // Escape quotes using backslash.

function escapeTitle(text) {
  return text.replace(/"/g, '\\"');
}

const MarkupGenerator =
  /*#__PURE__*/
  (function () {
    function MarkupGenerator(contentState, options) {
      _classCallCheck(this, MarkupGenerator);

      _defineProperty(this, 'blocks', void 0);

      _defineProperty(this, 'contentState', void 0);

      _defineProperty(this, 'currentBlock', void 0);

      _defineProperty(this, 'output', void 0);

      _defineProperty(this, 'totalBlocks', void 0);

      _defineProperty(this, 'listItemCounts', void 0);

      _defineProperty(this, 'options', void 0);

      this.contentState = contentState;
      this.options = options || defaultOptions;
    }

    _createClass(MarkupGenerator, [
      {
        key: 'generate',
        value: function generate() {
          this.output = [];
          this.blocks = this.contentState.getBlockMap().toArray();
          this.totalBlocks = this.blocks.length;
          this.currentBlock = 0;
          this.listItemCounts = {};

          while (this.currentBlock < this.totalBlocks) {
            this.processBlock();
          }

          let outputStr = this.output.join('');
          // bullets or numbers list will have more one newline between items when manual input
          // So, we will convert correct markdown formatting for them
          outputStr = outputStr
            .replace(
              /(^[+-] +[^\n]+(?:\n(?! +\n)[^\n]+)*)(\n\n)(?=[+-] )/gm,
              (_match, p1) => p1 + '\n'
            )
            .replace(
              /(^\d{1,9}[.)] +[^\n]+(?:\n(?! +\n)[^\n]+)*)(\n\n)(?=\d{1,9}[.)] )/gm,
              (_match, p1) => p1 + '\n'
            );
          return outputStr;
        },
      },
      {
        key: 'processBlock',
        value: function processBlock() {
          const block = this.blocks[this.currentBlock];
          const blockType = block.getType();

          switch (blockType) {
            case BLOCK_TYPE.HEADER_ONE: {
              this.insertLineBreaks(1);
              this.output.push('# ' + this.renderBlockContent(block) + '\n');
              break;
            }

            case BLOCK_TYPE.HEADER_TWO: {
              this.insertLineBreaks(1);
              this.output.push('## ' + this.renderBlockContent(block) + '\n');
              break;
            }

            case BLOCK_TYPE.HEADER_THREE: {
              this.insertLineBreaks(1);
              this.output.push('### ' + this.renderBlockContent(block) + '\n');
              break;
            }

            case BLOCK_TYPE.HEADER_FOUR: {
              this.insertLineBreaks(1);
              this.output.push('#### ' + this.renderBlockContent(block) + '\n');
              break;
            }

            case BLOCK_TYPE.HEADER_FIVE: {
              this.insertLineBreaks(1);
              this.output.push('##### ' + this.renderBlockContent(block) + '\n');
              break;
            }

            case BLOCK_TYPE.HEADER_SIX: {
              this.insertLineBreaks(1);
              this.output.push('###### ' + this.renderBlockContent(block) + '\n');
              break;
            }

            case BLOCK_TYPE.UNORDERED_LIST_ITEM: {
              const blockDepth = block.getDepth();
              const lastBlock = this.getLastBlock();
              const lastBlockType = lastBlock ? lastBlock.getType() : null;
              const lastBlockDepth =
                lastBlock && canHaveDepth(lastBlockType) ? lastBlock.getDepth() : null;

              if (lastBlockType !== blockType && lastBlockDepth !== blockDepth - 1) {
                this.insertLineBreaks(1); // Insert an additional line break if following opposite list type.

                if (lastBlockType === BLOCK_TYPE.ORDERED_LIST_ITEM) {
                  this.insertLineBreaks(1);
                }
              }

              const indent = ' '.repeat(block.depth * 4);
              this.output.push(indent + '- ' + this.renderBlockContent(block) + '\n');
              break;
            }

            case BLOCK_TYPE.ORDERED_LIST_ITEM: {
              const _blockDepth = block.getDepth();

              const _lastBlock = this.getLastBlock();

              const _lastBlockType = _lastBlock ? _lastBlock.getType() : null;

              const _lastBlockDepth =
                _lastBlock && canHaveDepth(_lastBlockType) ? _lastBlock.getDepth() : null;

              if (_lastBlockType !== blockType && _lastBlockDepth !== _blockDepth - 1) {
                this.insertLineBreaks(1); // Insert an additional line break if following opposite list type.

                if (_lastBlockType === BLOCK_TYPE.UNORDERED_LIST_ITEM) {
                  this.insertLineBreaks(1);
                }
              }

              const _indent = ' '.repeat(_blockDepth * 4); // TODO: figure out what to do with two-digit numbers

              const count = this.getListItemCount(block) % 10;
              this.output.push(
                _indent + ''.concat(count, '. ') + this.renderBlockContent(block) + '\n'
              );
              break;
            }

            case BLOCK_TYPE.BLOCKQUOTE: {
              this.insertLineBreaks(1);
              this.output.push(' > ' + this.renderBlockContent(block) + '\n');
              break;
            }

            case BLOCK_TYPE.CODE: {
              this.insertLineBreaks(1);

              if (this.options.gfm) {
                const language =
                  block.getData() && block.getData().get('language')
                    ? block.getData().get('language')
                    : '';
                this.output.push('```'.concat(language, '\n'));
                this.output.push(this.renderBlockContent(block) + '\n');
                this.output.push('```\n');
              } else {
                this.output.push(CODE_INDENT + this.renderBlockContent(block) + '\n');
              }

              break;
            }

            default: {
              this.insertLineBreaks(1);
              this.output.push(this.renderBlockContent(block) + '\n');
              break;
            }
          }

          this.currentBlock += 1;
        },
      },
      {
        key: 'getLastBlock',
        value: function getLastBlock() {
          return this.blocks[this.currentBlock - 1];
        },
      },
      {
        key: 'getNextBlock',
        value: function getNextBlock() {
          return this.blocks[this.currentBlock + 1];
        },
      },
      {
        key: 'getListItemCount',
        value: function getListItemCount(block) {
          const blockType = block.getType();
          const blockDepth = block.getDepth(); // To decide if we need to start over we need to backtrack (skipping list
          // items that are of greater depth)

          let index = this.currentBlock - 1;
          let prevBlock = this.blocks[index];

          while (
            prevBlock &&
            canHaveDepth(prevBlock.getType()) &&
            prevBlock.getDepth() > blockDepth
          ) {
            index -= 1;
            prevBlock = this.blocks[index];
          }

          if (
            !prevBlock ||
            prevBlock.getType() !== blockType ||
            prevBlock.getDepth() !== blockDepth
          ) {
            this.listItemCounts[blockDepth] = 0;
          }

          return (this.listItemCounts[blockDepth] = this.listItemCounts[blockDepth] + 1);
        },
      },
      {
        key: 'insertLineBreaks',
        value: function insertLineBreaks(n) {
          if (this.currentBlock > 0) {
            for (let i = 0; i < n; i++) {
              this.output.push('\n');
            }
          }
        },
      },
      {
        key: 'renderBlockContent',
        value: function renderBlockContent(block) {
          const contentState = this.contentState;
          const blockType = block.getType();
          let text = block.getText();
          // Trim WhiteSpace when convert text line to markdown
          if (/^\s+|\s+$/gm.test(text) && text.trim() !== '') {
            text = text.trim();
          }

          if (text === '') {
            // Prevent element collapse if completely empty.
            // TODO: Replace with constant.
            return '\u200B';
          }

          const charMetaList = block.getCharacterList();
          const entityPieces = getEntityRanges(text, charMetaList);
          return entityPieces
            .map(function (_ref) {
              const _ref2 = _slicedToArray(_ref, 2),
                entityKey = _ref2[0],
                stylePieces = _ref2[1];

              let content = stylePieces
                .map(function (_ref3) {
                  const _ref4 = _slicedToArray(_ref3, 2),
                    text = _ref4[0],
                    style = _ref4[1];

                  // Don't allow empty inline elements.
                  if (!text) {
                    return '';
                  }

                  let content = text; // Don't encode any text inside a code block.

                  if (blockType === BLOCK_TYPE.CODE) {
                    return content;
                  }

                  const contentLength = content.length;
                  content = content.trimLeft();
                  const leftPad = contentLength - content.length;
                  content = content.trimRight();
                  const rightPad = contentLength - content.length - leftPad;
                  if (!content.length) {
                    // Return the original text if there is nothing left after trimming.
                    return text;
                  }

                  // NOTE: We attempt some basic character escaping here, although
                  // I don't know if escape sequences are really valid in markdown,
                  // there's not a canonical spec to lean on.

                  if (style.has(CODE)) {
                    return '`' + encodeCode(content) + '`';
                  }

                  content = encodeContent(content);

                  if (style.has(BOLD)) {
                    content = '**'.concat(content, '**');
                  }

                  if (style.has(UNDERLINE)) {
                    // TODO: encode `+`?
                    content = '++'.concat(content, '++');
                  }

                  if (style.has(ITALIC)) {
                    content = '*'.concat(content, '*');
                  }

                  if (style.has(STRIKETHROUGH)) {
                    // TODO: encode `~`?
                    content = '~~'.concat(content, '~~');
                  }

                  content = content.padStart(content.length + leftPad);
                  return content.padEnd(content.length + rightPad);
                })
                .join('');

              const entity = entityKey ? contentState.getEntity(entityKey) : null;

              if (entity != null && entity.getType() === ENTITY_TYPE.LINK) {
                let text = content;
                const textLength = text.length;
                text = text.trimLeft();
                const leftPad = textLength - text.length;
                text = text.trimRight();
                const rightPad = textLength - text.length - leftPad;
                if (!text.length) {
                  return content;
                }

                const data = entity.getData();
                const url = data.href || data.url || '';
                // TODO: Not support storing title of Link in markdown
                //var title = data.title ? " \"".concat(escapeTitle(data.title), "\"") : '';
                //content = "[".concat(text, "](").concat(encodeURL(url)).concat(title, ")");
                content = '['.concat(text, '](').concat(encodeURL(url), ')');

                content = content.padStart(content.length + leftPad);
                return content.padEnd(content.length + rightPad);
              } else if (entity != null && entity.getType() === ENTITY_TYPE.BUTTON) {
                const _data = entity.getData();
                const text = _data.text ? ''.concat(escapeTitle(_data.text)) : '';
                const align = _data.align ? 'align:'.concat(_data.align) : 'align:center';
                let button = 'B['.concat(text, ']()(').concat(align, ')');
                const url = _data.url || '';
                const link = linkify.match(url);
                const field = url.match(fieldRegex);
                if (
                  (typeof link !== 'undefined' && link !== null) ||
                  (typeof field !== 'undefined' && field !== null)
                ) {
                  button = 'B['.concat(text, '](').concat(encodeURL(url), ')(').concat(align, ')');
                }
                return button;
              } else if (entity != null && entity.getType() === ENTITY_TYPE.IMAGE) {
                const _data = entity.getData();

                const src = _data.src || '';
                const alt = _data.alt ? ''.concat(escapeTitle(_data.alt)) : '';
                let image = '!['.concat(alt, '](').concat(encodeURL(src), ')');
                // TODO: DraftJs don't support LINK ENTITY contains IMAGE ENTITY
                // If IMAGE has url attribute, we will set it as linked image
                // stateFromElement supports alt, url and src attribute. So, We can use url to store url
                const url = _data.url || '';
                const link = linkify.match(url);
                const field = url.match(fieldRegex);
                if (
                  (typeof link !== 'undefined' && link !== null) ||
                  (typeof field !== 'undefined' && field !== null)
                ) {
                  image = '![]('.concat(encodeURL(src), ')');
                  image = '['.concat(image, '](').concat(encodeURL(url), ')');
                }
                return image;
              } else if (entity != null && entity.getType() === ENTITY_TYPE.EMBED) {
                return entity.getData().url || content;
              } else {
                return content;
              }
            })
            .join('');
        },
      },
    ]);

    return MarkupGenerator;
  })();

export default function stateToMarkdown(content, options) {
  return new MarkupGenerator(content, options).generate();
}
